import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import favIcon from "../assets/favIcon.svg";

import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";

export const Navbar = () => {
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("");
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleHamburgerClick = () => {
    setMenuOpen(!menuOpen);
  };

  const handleMenuItemClick = (action) => {
    if (action === "navigate") {
      setMenuOpen(false);
    }
  };

  return (
    <nav className="w-full sticky-navbar mt-8 py-4 bg-white shadow-lg text-lime-800">
      <div
        className="container mx-auto px-4 flex items-center justify-between"
        ref={menuRef}
      >
        {/* Logo & Desktop text */}
        <div className="flex-1 md:flex md:items-center">
          <Link
            to="/"
            className="flex items-center space-x-4 text-2xl font-bold mx-auto md:mx-0"
            onClick={() => setActiveMenu("home")}
          >
            <img
              src={favIcon}
              alt="JOTR Logo"
              className="h-10 w-auto"
            />
            <span className="hidden md:block text-2xl font-black text-lime-600">
              JOTR 2026
            </span>
          </Link>
        </div>

        {/* Mobile centered text */}
        <span className="block md:hidden absolute left-1/2 transform -translate-x-1/2 text-base font-semibold oneline whitespace-nowrap">
          JOTR SEATRC
        </span>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center space-x-8 md:space-x-4 text-base uppercase font-medium px-8 md:px-04">
          <Link
            to="/"
            className={`hover:text-lime-600 ${activeMenu === "home" ? "border-b-2 border-lime-600" : ""}`}
            onClick={() => setActiveMenu("home")}
          >
            HOME
          </Link>

          <Link
            to="/tiento"
            className={`hover:text-lime-600 ${activeMenu === "tiento" ? "border-b-2 border-lime-600" : ""}`}
            onClick={() => setActiveMenu("tiento")}
          >
            TIENTO X JOTR
          </Link>

          <Link
            to="/schedule"
            className={`hover:text-lime-600 ${activeMenu === "schedule" ? "border-b-2 border-lime-600" : ""}`}
            onClick={() => setActiveMenu("schedule")}
          >
            SCHEDULE
          </Link>

          <Link
            to="/entrylistpage"
            className={`hover:text-lime-600 ${activeMenu === "entrylist" ? "border-b-2 border-lime-600" : ""}`}
            onClick={() => setActiveMenu("entrylist")}
          >
            ENTRY LIST
          </Link>
          <Link
            to="/Shuttle"
            className={`hover:text-lime-600 ${activeMenu === "shuttle" ? "border-b-2 border-lime-600" : ""}`}
            onClick={() => setActiveMenu("shuttle")}
          >
            SHUTTLE
          </Link>
          <Link
            to="/hotel"
            className={`hover:text-lime-600 ${activeMenu === "hotel" ? "border-b-2 border-lime-600" : ""}`}
            onClick={() => setActiveMenu("hotel")}
          >
            HOTEL
          </Link>
          <Link
            to="/contact"
            className={`hover:text-lime-600 ${activeMenu === "contact" ? "border-b-2 border-lime-600" : ""}`}
            onClick={() => setActiveMenu("contact")}
          >
            CONTACT
          </Link>
        </div>

        {/* Desktop: user links including MY REGISTRATION and PROFILE (PROFILE placed last/rightmost) */}
        <div className="hidden md:flex items-center space-x-4">
          {user ? (
            <>
              <Link
                to="/MyRegistration"
                className="text-base uppercase font-medium hover:text-lime-600"
                onClick={() => setActiveMenu("myregistration")}
              >
                MY REGISTRATION
              </Link>
                <Link
                to="/Profile"
                className="text-base uppercase font-medium hover:text-lime-600"
                onClick={() => setActiveMenu("profile")}
              >
                PROFILE
              </Link>
              {(user?.role === "admin" || user?.role === "superadmin") && (
                <Link
                  to="/admin"
                  className="text-base uppercase font-medium hover:text-lime-600 font-bold text-lime-700"
                  onClick={() => setActiveMenu("admin")}
                >
                  ADMIN
                </Link>
              )}
            </>
          ) : (
            <Link
              to="/login"
              className="text-base uppercase font-medium hover:text-lime-600"
              onClick={() => setActiveMenu("login")}
            >
              LOGIN
            </Link>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden focus:outline-none"
          onClick={handleHamburgerClick}
        >
          <svg
            className="w-8 h-8"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            {menuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile dropdown */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 w-full bg-white shadow-lg md:hidden min-h-screen"
          >
            <div className="flex flex-col text-center w-full px-4 uppercase font-medium divide-y divide-slate-200 max-w-lg mx-auto">
              <div className="flex flex-col py-4 space-y-2">
                <Link
                  to="/"
                  className="py-2 hover:text-lime-600"
                  onClick={() => {
                    setActiveMenu("home");
                    handleMenuItemClick("navigate");
                  }}
                >
                  HOME
                </Link>

                <Link
                  to="/tiento"
                  className="py-2 hover:text-lime-600"
                  onClick={() => {
                    setActiveMenu("tiento");
                    handleMenuItemClick("navigate");
                  }}
                >
                  TIENTO X JOTR
                </Link>

                <Link
                  to="/schedule"
                  className="py-2 hover:text-lime-600"
                  onClick={() => {
                    setActiveMenu("schedule");
                    handleMenuItemClick("navigate");
                  }}
                >
                  SCHEDULE
                </Link>

                <Link
                  to="/entrylistpage"
                  className="py-2 hover:text-lime-600"
                  onClick={() => {
                    setActiveMenu("entrylist");
                    handleMenuItemClick("navigate");
                  }}
                >
                  ENTRY LIST
                </Link>

                <Link
                  to="/Shuttle"
                  className="py-2 hover:text-lime-600"
                  onClick={() => {
                    setActiveMenu("shuttle");
                    handleMenuItemClick("navigate");
                  }}
                >
                  SHUTTLE
                </Link>

                <Link
                  to="/hotel"
                  className="py-2 hover:text-lime-600"
                  onClick={() => {
                    setActiveMenu("hotel");
                    handleMenuItemClick("navigate");
                  }}
                >
                  HOTEL
                </Link>

                <Link
                  to="/contact"
                  className="py-2 hover:text-lime-600"
                  onClick={() => {
                    setActiveMenu("contact");
                    handleMenuItemClick("navigate");
                  }}
                >
                  CONTACT
                </Link>

                {(user?.role === "admin" || user?.role === "superadmin") && (
                  <Link
                    to="/admin"
                    className="py-2 hover:text-lime-600"
                    onClick={() => {
                      setActiveMenu("admin");
                      handleMenuItemClick("navigate");
                    }}
                  >
                    ADMIN
                  </Link>
                )}
              </div>

              {/* Profile Related Links for mobile (PROFILE last/rightmost in desktop; listed last in mobile block) */}
              {user ? (
                <div className="flex flex-col py-4 space-y-2">
                  <Link
                    to="/MyRegistration"
                    className="py-2 text-lime-800 hover:text-lime-600"
                    onClick={() => handleMenuItemClick("navigate")}
                  >
                    MY REGISTRATION
                  </Link>
                  <Link
                    to="/Profile"
                    className="py-2 text-lime-800 hover:text-lime-600"
                    onClick={() => handleMenuItemClick("navigate")}
                  >
                    PROFILE
                  </Link>
                </div>
              ) : (
                <div className="flex flex-col py-4 space-y-2">
                  <Link
                    to="/login"
                    className="py-2 text-lime-800 hover:text-lime-600"
                    onClick={() => {
                      setActiveMenu("login");
                      handleMenuItemClick("navigate");
                    }}
                  >
                    LOGIN
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
